import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppContext from '../../context/AppContext';
import { useMap } from 'react-leaflet';
import { apiGet } from '../../util/HttpApi';
import L from 'leaflet';
import styles from '../../menu/search/search.module.css';
import 'leaflet-spin';
import { Box, IconButton, Modal, Table, TableBody, TableCell, TableRow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import 'leaflet.markercluster';
import { useTranslation } from 'react-i18next';
import { areSetsEqual } from '../../util/Utils';
import { debouncer } from '../../context/TracksRoutingCache';
import {
    clusterMarkers,
    addMarkerTooltip,
    EXPLORE_BIG_ICON_SIZE,
    removeTooltip,
    SIMPLE_ICON_SIZE,
    EXPLORE_BIG_REAL_ICON_SIZE,
} from '../util/Clusterizer';
import { useSelectMarkerOnMap } from '../../util/hooks/map/useSelectMarkerOnMap';
import { hideMarkersUnderPin } from '../util/MarkerSelectionService';
import { getPhotoUrl } from '../../menu/search/explore/PhotoGallery';
import { WIKI_PLACE_PHOTO_SIZE } from '../../menu/search/explore/WikiPlacesItem';
import { getVisibleBboxInfo, mapSpinOptionsForVisibleBbox } from './MapStateLayer';
import { panToIfNeeded } from '../util/MapManager';
import { SimpleDotMarker } from '../markers/SimpleDotMarker';
import { EXPLORE_OBJS_KEY, useRecentDataSaver } from '../../util/hooks/menu/useRecentDataSaver';
import { getIconNameForPoiType, navigateToPoi } from '../../manager/PoiManager';
import { getPoiByOsmIdApi } from '../../manager/SearchApi';
import { getWikiLangs } from '../../util/lang';
import {
    FINAL_POI_ICON_NAME,
    ICON_KEY_NAME,
    TYPE_OSM_TAG,
    TYPE_OSM_VALUE,
} from '../../infoblock/components/wpt/WptTagsProvider';
import { useNavigate } from 'react-router-dom';
import { BBOX_COORDS_DECIMALS, EXPLORE_LAYER_ID, NAVIGATE_URL } from '../../manager/GlobalManager';
import { MARKER_Z_INDEX_MAIN } from '../util/ZIndexes';
import { NAVIGATION_OBJECT_TYPE_SEARCH } from '../../manager/NavigationManager';

export const EXPLORE_MIN_ZOOM = 6;

export function updateMarkerZIndex(layerGroup, zIndex) {
    layerGroup.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
            layer.setZIndexOffset(zIndex);
        }
    });
}

export function getImgByProps(props) {
    return props.photoTitle || props.depTitle;
}

export function getPlaceMarkerInfo(ctx, place) {
    const latlng = L.latLng(place.geometry.coordinates[1], place.geometry.coordinates[0]);
    const imgTag = ctx.searchSettings.useWikiImages ? place.properties.imageTitle : getImgByProps(place.properties);
    const photoUrl = getPhotoUrl({ photoTitle: imgTag, size: WIKI_PLACE_PHOTO_SIZE });

    return { latlng, photoUrl };
}

export default function ExploreLayer() {
    const ctx = useContext(AppContext);
    const map = useMap();

    const navigate = useNavigate();

    const GET_OBJ_DEBOUNCE_MS = 500;

    const timerRef = useRef(null);

    const filtersRef = useRef(null);
    const openedPoiRef = useRef(null);

    const { i18n } = useTranslation();

    const mainIconsLayerRef = useRef(null);
    const otherIconsLayerRef = useRef(null);
    const [mainIconsLayer, setMainIconsLayer] = useState(null);
    const [otherIconsLayer, setOtherIconsLayer] = useState(null);

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedObj, setSelectedObj] = useState(null);
    const [selectFromMap, setSelectFromMap] = useState(false);

    const zoom = map.getZoom();

    useEffect(() => {
        if (zoom < EXPLORE_MIN_ZOOM) {
            ctx.setWikiPlaces(null);
            removeLayers();
        }
        hideMarkersUnderPin(map, ctx);
    }, [zoom]);

    const getExploreLayers = useCallback(() => {
        if (mainIconsLayerRef.current && otherIconsLayerRef.current) {
            return [...mainIconsLayerRef.current.getLayers(), ...otherIconsLayerRef.current.getLayers()];
        }
        return null;
    }, [mainIconsLayer, otherIconsLayer]);
    useSelectMarkerOnMap({
        ctx,
        getLayers: getExploreLayers,
        type: EXPLORE_LAYER_ID,
        map,
    });
    const recentSaver = useRecentDataSaver();

    function closeModal() {
        setModalIsOpen(false);
    }

    function removeLayers() {
        if (mainIconsLayerRef.current) {
            mainIconsLayerRef.current.clearLayers();
        }
        if (otherIconsLayerRef.current) {
            otherIconsLayerRef.current.clearLayers();
        }
        // remove lost markers
        map.eachLayer((layer) => {
            if (layer instanceof L.MarkerClusterGroup) {
                map.removeLayer(layer);
            }
        });

        removeTooltip(map, ctx.searchTooltipRef);

        if (ctx.searchPointerRef?.current) {
            ctx.searchPointerRef.current.remove();
        }
    }

    useEffect(() => {
        const item = ctx.searchSettings.getPoi;
        async function getWikiPoi() {
            const response = await getPoiByOsmIdApi({
                lat: item.geometry.coordinates[1],
                lon: item.geometry.coordinates[0],
                osmid: item.properties?.osmid,
                type: item.properties?.osmtype,
            });
            const key = item.properties?.osmid ?? item.geometry.coordinates[1] + item.geometry.coordinates[0];
            const poi = response?.data ?? null;
            const obj = { poi, wikidata: item, key, mapObj: selectFromMap };
            recentSaver(EXPLORE_OBJS_KEY, obj);
            navigateToPoi(obj, navigate, true);
            ctx.setSelectedWpt({ ...obj, id: obj.wikidata?.properties?.id });
            if (!selectFromMap) {
                ctx.setSelectedPoiObj(obj);
            }
            setSelectFromMap(false);
        }

        if (item) {
            getWikiPoi().then(() => {
                ctx.setLoadingContextMenu(false);
                ctx.setLoadingContextItem(null);
                map.spin(false);
            });
        } else {
            openedPoiRef.current = null;
        }
    }, [ctx.searchSettings.getPoi]);

    useEffect(() => {
        const coords = ctx.selectedWpt?.wikidata?.geometry?.coordinates;
        if (!coords || coords.length < 2) return;
        panToIfNeeded({ map, latlng: { lat: coords[1], lng: coords[0] }, ctx });
    }, [ctx.selectedWpt?.wikidata?.properties?.id]);

    useEffect(() => {
        let ignore = false;
        let controller = new AbortController();
        const settings = ctx.searchSettings;
        const setLoadingContextMenu = ctx.setLoadingContextMenu;

        if (!ctx.exploreMenu && !ctx.searchSettings.showExploreMarkers && !settings.useWikiImages) {
            removeLayers();
            ctx.setWikiPlaces(null);
            return;
        }

        if (
            !settings.selectedFilters ||
            !ctx.wikiPlaces ||
            !areSetsEqual(filtersRef.current, settings.selectedFilters)
        ) {
            filtersRef.current = settings.selectedFilters ?? null;
            removeLayers();
            debouncer(
                () => runGetData({ controller, ignore, settings, setLoadingContextMenu }),
                timerRef,
                GET_OBJ_DEBOUNCE_MS
            );
        }

        const onMapMoveEnd = async () => {
            if (
                settings.useWikiImages ||
                settings.showExploreMarkers ||
                (ctx.exploreMenu && (mainIconsLayerRef.current || otherIconsLayerRef.current))
            ) {
                debouncer(
                    () => runGetData({ controller, ignore, settings, setLoadingContextMenu }),
                    timerRef,
                    GET_OBJ_DEBOUNCE_MS
                );
            }
        };
        map.on('moveend', onMapMoveEnd);

        return () => {
            ignore = true;
            controller.abort();
            map.off('moveend', onMapMoveEnd);
        };
    }, [
        ctx.exploreMenu,
        ctx.searchSettings.useWikiImages,
        ctx.searchSettings.selectedFilters,
        ctx.searchSettings.showExploreMarkers,
        ctx.setLoadingContextMenu,
    ]);

    /**
     * A debounced function to fetch place data from a specified API based on map boundaries and user-selected filters.
     * It only executes if not ignored and if at least one filter is selected.
     * The function also manages a loading spinner on the map during the data fetching process.
     *
     * @param {Object} params - Parameters for the fetch operation.
     * @param {AbortController.signal} params.controller - Used to abort the fetch request if necessary.
     * @param {boolean} params.ignore - If true, the fetch operation is skipped.
     * @param {Object} params.settings - Contains user settings such as `selectedFilters` and whether to use Wiki images.
     * @returns {Promise<void>} - A promise that resolves when the fetch operation is complete.
     */

    async function getData({ controller, ignore, settings, setLoadingContextMenu }) {
        const API_GET_OBJS = 'get-wiki-data';
        const API_GET_IMGS = 'get-wiki-images';
        if (!ignore) {
            if (map.getZoom() < EXPLORE_MIN_ZOOM || settings?.selectedFilters?.size === 0) {
                ctx.setWikiPlaces(null);
                return;
            }
            setLoadingContextMenu(true);
            const visible = getVisibleBboxInfo(ctx, map);
            if (!visible) {
                setLoadingContextMenu(false);
                return;
            }
            const bbox = visible.bounds;
            const api = settings?.useWikiImages ? API_GET_IMGS : API_GET_OBJS;
            const response = await apiGet(`${process.env.REACT_APP_USER_API_SITE}/search/${api}`, {
                apiCache: true,
                params: {
                    northWest: `${Number(bbox.getNorthWest().lat).toFixed(BBOX_COORDS_DECIMALS)},${Number(bbox.getNorthWest().lng).toFixed(BBOX_COORDS_DECIMALS)}`,
                    southEast: `${Number(bbox.getSouthEast().lat).toFixed(BBOX_COORDS_DECIMALS)},${Number(bbox.getSouthEast().lng).toFixed(BBOX_COORDS_DECIMALS)}`,
                    lang: settings?.useWikiImages ? null : getWikiLangs(i18n.language),
                    filters: settings?.selectedFilters ? [...settings.selectedFilters] : null,
                },
                signal: controller.signal,
            });
            if (response?.data) {
                let jsonData = response.data.features;
                ctx.setWikiPlaces(jsonData);
            } else {
                ctx.setWikiPlaces(null);
            }
            setLoadingContextMenu(false);
            removeTooltip(map, ctx.searchTooltipRef);
        }
    }

    function runGetData(params) {
        getData(params).catch((e) => {
            if (!params.controller.signal.aborted) {
                throw e;
            }
        });
    }

    function openInfo(e, feature) {
        if (ctx.searchSettings.useWikiImages) {
            setSelectedObj(feature);
            setModalIsOpen(true);
        } else if (openedPoiRef.current !== feature) {
            openedPoiRef.current = feature;
            ctx.setLoadingContextMenu(true);
            map.spin(true, mapSpinOptionsForVisibleBbox(map, ctx));
            setSelectFromMap(true);
            ctx.setSearchSettings({ ...ctx.searchSettings, getPoi: feature });
        }

        ctx.setPhotoGallery(null);
    }

    const markerClusterGroup = new L.MarkerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        maxClusterRadius: 100,
        iconCreateFunction: function (cluster) {
            let count = cluster.getChildCount();
            if (count === 1) {
                return cluster.getAllChildMarkers()[0].options.icon;
            } else {
                const childMarkers = cluster.getAllChildMarkers();
                const minIndexMarker = childMarkers.reduce((min, m) => (m.options.index < min.options.index ? m : min));
                for (const marker of childMarkers) {
                    if (marker.options.index === minIndexMarker.options.index && marker.options.icon) {
                        return marker.options.icon;
                    }
                }
            }
            return L.divIcon({
                className: 'dot-icon',
                simple: true,
                iconSize: [8, 8],
                html: '<div class="dot"></div>',
            });
        },
    });

    const handleClusterClick = useCallback((cluster) => {
        const childMarkers = cluster.layer.getAllChildMarkers();
        const minIndexMarker = childMarkers.reduce((min, m) => (m.options.index < min.options.index ? m : min));
        const mainMarker = childMarkers.find((m) => m.options.index === minIndexMarker.options.index);
        if (mainMarker) {
            mainMarker.fire('click');
        }
    }, []);

    useEffect(() => {
        markerClusterGroup.on('clusterclick', handleClusterClick);

        return () => {
            markerClusterGroup.off('clusterclick', handleClusterClick);
        };
    }, [handleClusterClick]);

    function createGeoJsonData(places) {
        const sortedPlaces = [...places].sort((a, b) => a.properties.rowNum - b.properties.rowNum);
        return {
            type: 'FeatureCollection',
            features: sortedPlaces.map((place, index) => ({
                type: 'Feature',
                properties: { ...place.properties },
                index,
                geometry: {
                    type: 'Point',
                    coordinates: place.geometry.coordinates,
                },
            })),
        };
    }

    /**
     * Replaces old layers with new ones on the map. This function clears all layers in the provided oldLayers object before adding newLayers.
     * It is a more efficient approach when updating large sets of data, as it avoids the overhead of manually filtering through and updating individual layers.
     * @param {L.LayerGroup} oldLayers - The layer group to clear from the map.
     * @param {L.Layer} newLayers - The new layer to add to the map.
     * @returns {L.Layer} The newly added layers, allowing for further operations if needed.
     */
    function addLayers(oldLayers, newLayers) {
        if (oldLayers) {
            oldLayers.clearLayers();
        }
        newLayers.options = { ...newLayers.options, id: EXPLORE_LAYER_ID };
        map.addLayer(newLayers);
        hideMarkersUnderPin(map, ctx);
        return newLayers;
    }

    useEffect(() => {
        const abortController = new AbortController();

        if (!ctx.exploreMenu && !ctx.searchSettings.showExploreMarkers && !ctx.searchSettings.useWikiImages) {
            abortController.abort();
            return;
        }

        if (ctx.wikiPlaces) {
            const zoom = map.getZoom();
            const center = map.getCenter();
            const latitude = center.lat;
            const geoJsonData = createGeoJsonData(ctx.wikiPlaces);
            const { mainMarkers, secondaryMarkers } = clusterMarkers({
                places: geoJsonData.features,
                zoom,
                latitude,
                isExplore: true,
            });

            let simpleMarkersArr = new L.geoJSON();
            let largeMarkersArr = new L.geoJSON();

            const markerPromises = mainMarkers.map((place) => {
                const { latlng, photoUrl } = getPlaceMarkerInfo(ctx, place);

                return new Promise((resolve, reject) => {
                    if (abortController.signal.aborted) {
                        return resolve();
                    }

                    const image = new Image();
                    image.onload = () => {
                        if (abortController.signal.aborted) {
                            return resolve();
                        }

                        const icon = L.icon({
                            iconUrl: photoUrl,
                            iconSize: [EXPLORE_BIG_ICON_SIZE, EXPLORE_BIG_ICON_SIZE],
                            iconAnchor: [EXPLORE_BIG_REAL_ICON_SIZE / 2, EXPLORE_BIG_REAL_ICON_SIZE / 2],
                            className: `${styles.wikiIconLarge} ${styles.wikiIcon}`,
                        });
                        const marker = L.marker(latlng, {
                            icon,
                            index: place.index,
                            idObj: place.properties.id,
                            photoUrl,
                        });
                        addEventListeners({ marker, place, main: true, iconSize: EXPLORE_BIG_ICON_SIZE, latlng });
                        largeMarkersArr.addLayer(marker);
                        resolve();
                    };
                    image.onerror = () => {
                        if (abortController.signal.aborted) {
                            return resolve();
                        }

                        const circle = new SimpleDotMarker(latlng, place, {
                            idObj: place.properties.id,
                            photoUrl,
                            [FINAL_POI_ICON_NAME]: getIconNameForPoiType({
                                iconKeyName: place.properties?.[ICON_KEY_NAME],
                                typeOsmTag: place.properties?.[TYPE_OSM_TAG],
                                typeOsmValue: place.properties?.[TYPE_OSM_VALUE],
                            }),
                        }).build();
                        addEventListeners({ marker: circle, place, latlng });
                        largeMarkersArr.addLayer(circle);
                        resolve();
                    };
                    image.src = photoUrl;
                });
            });

            Promise.all(markerPromises)
                .then(() => {
                    for (const place of secondaryMarkers) {
                        const { latlng, photoUrl } = getPlaceMarkerInfo(ctx, place);

                        const circle = new SimpleDotMarker(latlng, place, {
                            idObj: place.properties.id,
                            photoUrl,
                            [FINAL_POI_ICON_NAME]: getIconNameForPoiType({
                                iconKeyName: place.properties?.[ICON_KEY_NAME],
                                typeOsmTag: place.properties?.[TYPE_OSM_TAG],
                                typeOsmValue: place.properties?.[TYPE_OSM_VALUE],
                            }),
                        }).build();
                        addEventListeners({ marker: circle, place, latlng });
                        simpleMarkersArr.addLayer(circle);
                    }

                    if (ctx.exploreMenu || ctx.searchSettings.showExploreMarkers || ctx.searchSettings.useWikiImages) {
                        otherIconsLayerRef.current = addLayers(otherIconsLayerRef.current, simpleMarkersArr);
                        mainIconsLayerRef.current = addLayers(mainIconsLayerRef.current, largeMarkersArr);
                        updateMarkerZIndex(mainIconsLayerRef.current, MARKER_Z_INDEX_MAIN);
                        map.fire('explore-layers-updated');
                        setOtherIconsLayer(otherIconsLayerRef.current);
                        setMainIconsLayer(mainIconsLayerRef.current);
                    }
                })
                .catch((error) => {
                    if (error !== 'Operation aborted') {
                        console.error(error);
                    }
                });
        }

        return () => {
            abortController.abort();
        };
    }, [ctx.wikiPlaces, ctx.exploreMenu, ctx.searchSettings.showExploreMarkers]);

    function addEventListeners({ marker, place, main = false, latlng, iconSize = SIMPLE_ICON_SIZE }) {
        // Add click event to open information about the place
        marker.on('click', (e) => {
            if (globalThis.location.pathname.includes(NAVIGATE_URL)) {
                const lat = e.latlng?.lat ?? latlng?.lat;
                const lon = e.latlng?.lng ?? latlng?.lng;

                if (lat && lon) {
                    const wpt = {
                        ...place.properties,
                        lat,
                        lng: lon,
                        type: NAVIGATION_OBJECT_TYPE_SEARCH,
                    };
                    const navEvent = new CustomEvent('nav-marker-click', {
                        detail: {
                            wpt,
                            latlng: L.latLng(lat, lon),
                        },
                        cancelable: true,
                    });
                    navEvent.originalEvent = e.originalEvent;
                    globalThis.dispatchEvent(navEvent);

                    // If NavigationLayer handled it (found empty input), don't open info
                    if (navEvent.defaultPrevented) {
                        L.DomEvent.stopPropagation(e);
                        return;
                    }
                }
            }
            // Normal logic: open info
            openInfo(e, place);
        });

        const tooltipText = () => {
            if (place.properties.wikiTitle && place.properties.wikiTitle !== '') {
                return place.properties.wikiTitle;
            }
        };

        addMarkerTooltip({
            marker,
            setSelectedId: ctx.setSelectedWptId,
            mainStyle: main,
            text: tooltipText(),
            latlng,
            iconSize,
            map,
            ctx,
            type: EXPLORE_LAYER_ID,
        });
    }

    return (
        <>
            {selectedObj && (
                <Modal
                    open={modalIsOpen}
                    onClose={closeModal}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: 'background.paper',
                            border: '1px solid #000',
                            boxShadow: 24,
                            p: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                        }}
                    >
                        <IconButton
                            variant="contained"
                            type="button"
                            sx={{ top: 0, right: 0, position: 'absolute', color: 'var(--svg-icon-color)' }}
                            onClick={closeModal}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box>
                            <img src={getPhotoUrl({ photo: selectedObj })} alt="Selected" />
                        </Box>
                        <Box sx={{ marginTop: 2 }}>
                            <Table size="small" aria-label="properties table">
                                <TableBody>
                                    {Object.entries(selectedObj.properties).map(([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell component="th" scope="row">
                                                {key == null ? 'N/A' : key.toString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                {value == null ? 'N/A' : value.toString()}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                </Modal>
            )}
        </>
    );
}
